import { format } from "date-fns";
import { Loader2, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Both roster dialogs: "Assign Staff" (shift assignment) and "New Leave Request".
 */
export function RosterShiftForm({
  // Assign staff dialog
  assignDialogOpen,
  onAssignDialogChange,
  selectedCell,
  getBranchName,
  availableStaff,
  selectedStaffId,
  onSelectStaffId,
  assignmentNotes,
  onAssignmentNotesChange,
  submitting,
  onAssignStaff,
  // Leave request dialog
  leaveDialogOpen,
  onLeaveDialogChange,
  staff,
  leaveForm,
  onLeaveFormChange,
  onCreateLeaveRequest,
}) {
  return (
    <>
      {/* Assign Staff Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={onAssignDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">Assign Staff</DialogTitle>
            <DialogDescription>
              {selectedCell && (
                <>
                  Assign a therapist to <strong>{getBranchName(selectedCell.branch_id)}</strong> on{" "}
                  <strong>{format(new Date(selectedCell.date), "EEEE, MMMM d")}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Select Staff *</Label>
              <Select value={selectedStaffId} onValueChange={onSelectStaffId}>
                <SelectTrigger data-testid="assign-staff-select">
                  <SelectValue placeholder="Choose a therapist" />
                </SelectTrigger>
                <SelectContent>
                  {availableStaff.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      No available staff
                    </SelectItem>
                  ) : (
                    availableStaff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Notes (optional)</Label>
              <Input
                value={assignmentNotes}
                onChange={(e) => onAssignmentNotesChange(e.target.value)}
                placeholder="e.g., Morning shift only"
                data-testid="assignment-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onAssignDialogChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={onAssignStaff}
              disabled={submitting || !selectedStaffId}
              className="bg-forest hover:bg-forest-light"
              data-testid="confirm-assign-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Request Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={onLeaveDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">New Leave Request</DialogTitle>
            <DialogDescription>Submit a leave request for a staff member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Staff Member *</Label>
              <Select
                value={leaveForm.staff_id}
                onValueChange={(v) => onLeaveFormChange({ staff_id: v })}
              >
                <SelectTrigger data-testid="leave-staff-select">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Start Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="leave-start-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(leaveForm.start_date, "PP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={leaveForm.start_date}
                      onSelect={(d) => d && onLeaveFormChange({ start_date: d, ...(leaveForm.end_date < d ? { end_date: d } : {}) })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">End Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="leave-end-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(leaveForm.end_date, "PP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={leaveForm.end_date}
                      disabled={(d) => d < leaveForm.start_date}
                      onSelect={(d) => d && onLeaveFormChange({ end_date: d })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Reason *</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => onLeaveFormChange({ reason: e.target.value })}
                placeholder="Enter reason for leave..."
                rows={3}
                data-testid="leave-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onLeaveDialogChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={onCreateLeaveRequest}
              disabled={submitting}
              className="bg-forest hover:bg-forest-light"
              data-testid="submit-leave-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
